import { getUserRoles } from '../../utils/authRoles';

const normalizeKey = (value) => (typeof value === 'string' ? value.trim().toLowerCase() : null);

const normalizeId = (value) => {
  if (value === null || value === undefined || value === '') return null;
  return String(value);
};

const normalizeEmail = (value) => {
  if (!value) return null;
  return String(value).trim().toLowerCase();
};

export const isHkeAdminUser = (user) => {
  const roles = getUserRoles(user);
  const legacyRole = normalizeKey(user?.role);
  const permissions = Array.isArray(user?.permissions) ? user.permissions : [];
  const hasManageLeadsPermission = permissions.some((permission) => {
    if (typeof permission === 'string') return normalizeKey(permission) === 'manage_leads';
    if (!permission || typeof permission !== 'object') return false;
    return (
      normalizeKey(permission?.key) === 'manage_leads' ||
      normalizeKey(permission?.name) === 'manage_leads' ||
      normalizeKey(permission?.permission) === 'manage_leads' ||
      normalizeKey(permission?.value) === 'manage_leads'
    );
  });

  return roles.includes('hke_admin') || legacyRole === 'hke_admin' || legacyRole === 'admin' || hasManageLeadsPermission;
};

const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object || {}, key);

const isUnassignedLead = (lead) => {
  const hasExplicitAssignmentIds = hasOwn(lead, 'consultant_id') || hasOwn(lead, 'consultant_advisor_id');
  if (hasExplicitAssignmentIds) {
    return normalizeId(lead?.consultant_id) === null && normalizeId(lead?.consultant_advisor_id) === null;
  }

  return lead?.consultant == null && lead?.consultant_advisor == null;
};

const getUserConsultantIds = (user) =>
  [user?.consultant_id, user?.consultant?.id].map(normalizeId).filter(Boolean);

const getLeadConsultantIds = (lead) =>
  [
    lead?.consultant_id,
    lead?.consultant?.id,
    lead?.consultant_advisor?.consultant_id,
  ]
    .map(normalizeId)
    .filter(Boolean);

const getUserAdvisorId = (user) =>
  normalizeId(
    user?.consultant_advisor_id ??
      user?.consultant_advisor?.id ??
      user?.advisor_id ??
      user?.advisor?.id
  );

const getLeadAdvisorId = (lead) =>
  normalizeId(
    lead?.consultant_advisor_id ??
      lead?.consultant_advisor?.id
  );

const getUserEmail = (user) => normalizeEmail(user?.email);

const getLeadAdvisorEmail = (lead) =>
  normalizeEmail(
    lead?.consultant_advisor?.email ??
      lead?.consultant_advisor_email ??
      null
  );

export const canEditLeadStatus = (user, lead) => {
  if (!user || !lead) return false;

  const roles = getUserRoles(user) || [];
  const normalizedRoles = roles.map((r) => String(r).toLowerCase());
  const legacyRole = normalizeKey(user?.role);

  // NOTE: precedence matters. If someone somehow has both roles,
  // we treat them as advisor first to avoid “advisor editing everything”.
  const isAdvisor = normalizedRoles.includes('consultant_advisor') || legacyRole === 'consultant_advisor';
  const isConsultantAdmin = normalizedRoles.includes('consultant_admin') || legacyRole === 'consultant_admin';
  const isHkeAdmin = normalizedRoles.includes('hke_admin') || legacyRole === 'hke_admin' || isHkeAdminUser(user);

  const advisorId = getUserAdvisorId(user);
  const leadAdvisorId = getLeadAdvisorId(lead);

  const userEmail = getUserEmail(user);
  const leadAdvisorEmail = getLeadAdvisorEmail(lead);

  // TEMP debug (remove once confirmed)
  // eslint-disable-next-line no-console
  console.log('Lead permission debug', {
    roles,
    legacyRole,
    advisorId,
    leadAdvisorId,
    userEmail,
    leadAdvisorEmail,
    userConsultantIds: getUserConsultantIds(user),
    leadConsultantIds: getLeadConsultantIds(lead),
  });

  if (isAdvisor) {
    // advisor can only edit assigned-to-advisor leads, never unassigned
    if (!leadAdvisorId) return false;

    // primary: id match
    if (advisorId && advisorId === leadAdvisorId) return true;

    // fallback: email match (when the user payload does not include consultant_advisor_id)
    if (userEmail && leadAdvisorEmail && userEmail === leadAdvisorEmail) return true;

    return false;
  }

  if (isConsultantAdmin) {
    const userConsultantIds = getUserConsultantIds(user);
    const leadConsultantIds = getLeadConsultantIds(lead);
    return userConsultantIds.some((id) => leadConsultantIds.includes(id));
  }

  if (isHkeAdmin) {
    return isUnassignedLead(lead);
  }

  return false;
};